'use strict';
/**
 * visitor-id.js — canonical first-party visitor id minter.
 *
 * Creates + persists a stable `visitorID` (cookie, 30 days, AND localStorage),
 * generated once per browser the SAME way the ROW pilot does: a v4-style UUID,
 * minted UNCONDITIONALLY when absent. Every downstream consumer reads this id:
 *   - cae-client.js       getViewerId() -> localStorage 'viewerId' || 'visitorID' || cookie …
 *   - signup-vid-link.js  visitorId()   -> SE_visitorIdentity.visitor_id || localStorage 'visitorID'
 *   - the scarcity engine + the Sleeknote free-patch injector (localStorage 'visitorID')
 *
 * MUST load BEFORE cae-client.js so the id exists before viewerId is first read
 * (otherwise cae-client mints its OWN 'viewerId' and the ids diverge, breaking the
 * CAE <-> users_drip join). Idempotent: an existing valid id (cookie or localStorage)
 * is reused. The legacy junk id '1' is treated as absent (matches ROW's getCookie).
 *
 * Ported from the ROW pilot so IL/EU create the visitor id identically to ROW.
 */
(function () {
  var VID_KEY = 'visitorID';

  function generateUUID() {
    var d = new Date().getTime();
    var d2 = (typeof performance !== 'undefined' && performance.now && (performance.now() * 1000)) || 0;
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      if (d > 0) { r = (d + r) % 16 | 0; d = Math.floor(d / 16); }
      else if (d2 > 0) { r = (d2 + r) % 16 | 0; d2 = Math.floor(d2 / 16); }
      return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });
  }

  function getCookie(name) {
    try {
      var nameEQ = name + '=';
      var ca = document.cookie.split(';');
      for (var i = 0; i < ca.length; i++) {
        var c = ca[i].trim();
        if (c.indexOf(nameEQ) === 0) { return c.substring(nameEQ.length); }
      }
    } catch (e) {}
    return null;
  }

  function setCookie(name, value, days) {
    if (!value || value === '1') return;   // never persist the legacy junk id (matches ROW)
    try {
      var expires = '';
      if (days) {
        var date = new Date();
        date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
        expires = '; expires=' + date.toUTCString();
      }
      document.cookie = name + '=' + value + expires + '; path=/';
    } catch (e) {}
  }

  try {
    var vid = getCookie(VID_KEY);
    if (vid === '1') vid = null;                       // legacy junk -> treat as absent
    if (!vid) {
      try { vid = (localStorage.getItem(VID_KEY) || '').trim(); } catch (e) { vid = ''; }
      if (vid === '1') vid = null;
    }
    if (!vid) { vid = generateUUID(); }                // mint UNCONDITIONALLY when none exists

    // Mirror to BOTH cookie (30d) + localStorage so every consumer reads the same id.
    setCookie(VID_KEY, vid, 30);
    try { localStorage.setItem(VID_KEY, vid); } catch (e) {}
    try { window.AGB_VisitorId = vid; } catch (e) {}   // sync accessor for anything that wants it

    // ── Country (IL store) ────────────────────────────────────────────────
    // localStorage['country'] = what CAE (analytics `country` column) and
    // country-aware flows read. IL policy (user decisions 2026-07-05):
    // 'Israel' is written IMMEDIATELY (never null — IL shoppers are assumed
    // domestic), then Shopify's own IP-geo endpoint
    // /browsing_context_suggestions.json upgrades it async when the visitor is
    // actually abroad (~1% of IL orders bill foreign; without this the IL
    // analytics country column is a constant = zero information).
    // Deliberate divergences from the EU resolver:
    //   * NO window.Shopify.country tier — IL is a single-market store, so
    //     that value is constant 'IL' (it reflects the rendering market, not
    //     the visitor) and would mask the real geo.
    //   * NO db-app /api/whoami fallback — IL stays zero-backend; on any
    //     failure the already-written 'Israel' default simply stands.
    // Runs only when country is missing/poisoned (one fetch per browser).
    try {
      var cur = localStorage.getItem('country');
      if (!cur || cur === 'null' || cur === 'undefined') {
        try { localStorage.setItem('country', 'Israel'); } catch (e) {}
        var toName = function (codeOrName) {
          var s = String(codeOrName || '').trim();
          if (!s) return null;
          if (s.length !== 2) return s; // already a display name
          try {
            var n = new Intl.DisplayNames(['en'], { type: 'region' }).of(s.toUpperCase());
            return (n && n.toUpperCase() !== s.toUpperCase()) ? n : null;
          } catch (e) { return null; }
        };
        fetch('/browsing_context_suggestions.json', { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (d) {
            // Shopify has varied this response shape over time — same
            // defensive extraction as the EU resolver / the EU TTB.
            var code = d && (
              (d.detected_values && d.detected_values.country && (d.detected_values.country.handle || d.detected_values.country.value)) ||
              d.country_code ||
              (Array.isArray(d.suggestions) && (d.suggestions.filter(function (s) { return s && (s.part === 'country' || s.part === 'country_code'); })[0] || {}).value)
            ) || null;
            var name = toName(code);
            if (name && name !== 'Israel') {
              try { localStorage.setItem('country', name); } catch (e) {}
            }
          })
          .catch(function () { /* default 'Israel' stands */ });
      }
    } catch (e) {}
  } catch (e) { /* never block the page on id minting */ }
})();
